import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { ImportExport, type ImportColumn } from '../components/ImportExport';
import { useToast } from '../components/Toast';
import type { DepartmentDto, Institution, Staff, StaffRole } from '../types/models';

const GENDERS = ['Male', 'Female', 'Other'];

// Columns for the staff import/export sheet. Institution/Department/Staff-role are located by code.
const STAFF_COLUMNS: ImportColumn[] = [
  { header: 'Institution Code', key: 'institutionCode', sample: 'DIT' },
  { header: 'Department Code', key: 'departmentCode', sample: 'CSE' },
  { header: 'Staff Role Code', key: 'staffRoleCode', sample: 'PROF' },
  { header: 'Staff Code', key: 'staffCode', sample: 'EMP001' },
  { header: 'First Name', key: 'firstName', sample: 'Meena' },
  { header: 'Last Name', key: 'lastName', sample: 'Iyer' },
  { header: 'Email', key: 'email', sample: 'meena.iyer@college.edu' },
  { header: 'Mobile', key: 'mobile1', sample: '9876500000' },
  { header: 'Gender', key: 'gender', sample: 'Female' },
];
const emptyForm = {
  institutionId: '', departmentId: '', staffRoleId: '',
  firstName: '', lastName: '', email: '', mobile1: '', staffCode: '', gender: '',
};

export function StaffPage() {
  const toast = useToast();
  const { orgId = '' } = useParams();

  const [insts, setInsts] = useState<Institution[]>([]);
  const [depts, setDepts] = useState<DepartmentDto[]>([]);
  const [roles, setRoles] = useState<StaffRole[]>([]);
  const [staff, setStaff] = useState<Staff[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Filter bar (Institution → Department).
  const [filter, setFilter] = useState({ institutionId: '', departmentId: '' });

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function refresh() {
    try {
      const [i, d, r, s] = await Promise.all([
        api.listInstitutions(orgId), api.listDepartments(orgId),
        api.listStaffRoles(orgId), api.listStaff(orgId),
      ]);
      setInsts(i); setDepts(d); setRoles(r); setStaff(s);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load staff.');
    }
  }

  // Departments belonging to the institution currently chosen in the form.
  const deptsForForm = useMemo(
    () => depts.filter(d => d.institutionId === form.institutionId),
    [depts, form.institutionId],
  );

  const prerequisitesReady = insts.length > 0 && depts.length > 0 && roles.length > 0;

  // Filter-bar cascade + the filtered grid.
  const deptsForFilter = useMemo(
    () => depts.filter(d => d.institutionId === filter.institutionId),
    [depts, filter.institutionId],
  );

  function setFilterField(key: keyof typeof filter, value: string) {
    setFilter(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'institutionId') next.departmentId = '';
      return next;
    });
  }

  const filteredStaff = useMemo(
    () => (staff ?? []).filter(s =>
      (!filter.institutionId || s.institutionId === filter.institutionId)
      && (!filter.departmentId || s.departmentId === filter.departmentId)),
    [staff, filter],
  );

  function openCreate() {
    setFormError(null);
    setEditId(null);
    setForm({ ...emptyForm });
    setShowModal(true);
  }

  function openEdit(s: Staff) {
    setFormError(null);
    setEditId(s.id);
    setForm({
      institutionId: s.institutionId, departmentId: s.departmentId, staffRoleId: s.staffRoleId,
      firstName: s.firstName, lastName: s.lastName, email: s.email,
      mobile1: s.mobile1, staffCode: s.staffCode, gender: s.gender ?? '',
    });
    setShowModal(true);
  }

  function setField(key: keyof typeof emptyForm, value: string) {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      // Changing institution invalidates a department from another institution.
      if (key === 'institutionId') next.departmentId = '';
      return next;
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!/^\d{10}$/.test(form.mobile1.trim())) {
      setFormError('Mobile number must be exactly 10 digits.');
      return;
    }
    setBusy(true);
    const payload = {
      institutionId: form.institutionId, departmentId: form.departmentId, staffRoleId: form.staffRoleId,
      firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim(),
      mobile1: form.mobile1.trim(), staffCode: form.staffCode.trim(),
      gender: form.gender || null,
    };
    try {
      if (editId) await api.updateStaff(editId, payload);
      else await api.createStaff({ organizationId: orgId, ...payload });
      setShowModal(false);
      await refresh();
      toast.success(editId ? 'Staff updated.' : 'Staff created.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to save staff.';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function remove(s: Staff) {
    if (!confirm(`Delete staff “${s.firstName} ${s.lastName}”? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.deleteStaff(s.id);
      await refresh();
      toast.success('Staff deleted.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to delete staff.';
      setError(msg);
      toast.error(msg);
    }
  }

  return (
    <OrgLayout>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Staff</h1>
        <div className="row" style={{ gap: 8 }}>
          <ImportExport
            columns={STAFF_COLUMNS}
            templateName="staff-sample.xlsx"
            disabled={!prerequisitesReady}
            onImport={rows => api.importStaff(orgId, rows)}
            onDone={refresh}
            exportName="staff"
            exportTitle="Staff"
            exportRows={(filteredStaff ?? []).map((s, i) => ({
              'S.No': i + 1,
              'Staff Code': s.staffCode,
              'Name': `${s.firstName} ${s.lastName}`,
              'Email': s.email,
              'Mobile': s.mobile1,
              'Role': s.staffRoleName,
              'Department': s.departmentName,
              'Institution': s.institutionName,
            }))}
          />
          <button className="primary" onClick={openCreate} disabled={!prerequisitesReady}>+ Add staff</button>
        </div>
      </div>

      {!prerequisitesReady && staff && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="muted">
            To add staff you first need at least one{' '}
            <Link to={`/org/${orgId}/institutions`}>institution</Link>,{' '}
            <Link to={`/org/${orgId}/departments`}>department</Link>, and{' '}
            <Link to={`/org/${orgId}/staff-roles`}>staff role</Link>.
          </div>
        </div>
      )}

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 220px' }}>
            <div className="muted">Institution</div>
            <select value={filter.institutionId} onChange={e => setFilterField('institutionId', e.target.value)}>
              <option value="">All</option>
              {insts.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 220px' }}>
            <div className="muted">Department</div>
            <select value={filter.departmentId} disabled={!filter.institutionId}
                    onChange={e => setFilterField('departmentId', e.target.value)}>
              <option value="">All</option>
              {deptsForFilter.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
            </select>
          </label>
          {(filter.institutionId || filter.departmentId) && (
            <button onClick={() => setFilter({ institutionId: '', departmentId: '' })}>Clear</button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>S.no</th>
              <th>Staff Code</th>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Role</th>
              <th>Department</th>
              <th>Institution</th>
              <th style={{ width: 150 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!staff && <tr><td colSpan={9} className="muted">Loading…</td></tr>}
            {staff && filteredStaff.length === 0 && (
              <tr><td colSpan={9} className="muted">
                {staff.length === 0 ? 'No staff yet.' : 'No staff match the selected filters.'}
              </td></tr>
            )}
            {filteredStaff.map((s, idx) => (
              <tr key={s.id}>
                <td>{idx + 1}</td>
                <td className="slug">{s.staffCode}</td>
                <td style={{ fontWeight: 600 }}>{s.firstName} {s.lastName}</td>
                <td>{s.email}</td>
                <td>{s.mobile1}</td>
                <td>{s.staffRoleName}</td>
                <td>{s.departmentName}</td>
                <td>{s.institutionName}</td>
                <td>
                  <button className="link-btn" onClick={() => openEdit(s)}>Edit</button>
                  <button className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => remove(s)}>Delete</button>
                  <RecordInfoButton entity="staff" id={s.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-backdrop" onMouseDown={() => setShowModal(false)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()}>
            <div className="modal-head">
              <h2>{editId ? 'Edit staff' : 'Create staff'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)} aria-label="Close">✕</button>
            </div>
            <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">Institution *</div>
                  <select value={form.institutionId} required
                          onChange={e => setField('institutionId', e.target.value)}>
                    <option value="" disabled>Select…</option>
                    {insts.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
                  </select>
                </label>
                <label style={{ flex: 1 }}>
                  <div className="muted">Department *</div>
                  <select value={form.departmentId} required disabled={!form.institutionId}
                          onChange={e => setField('departmentId', e.target.value)}>
                    <option value="" disabled>{form.institutionId ? 'Select…' : 'Pick institution first'}</option>
                    {deptsForForm.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
                  </select>
                </label>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">Staff Role *</div>
                  <select value={form.staffRoleId} required
                          onChange={e => setField('staffRoleId', e.target.value)}>
                    <option value="" disabled>Select…</option>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.code} — {r.name}</option>)}
                  </select>
                </label>
                <label style={{ flex: 1 }}>
                  <div className="muted">Staff Code *</div>
                  <input value={form.staffCode} required maxLength={64}
                         onChange={e => setField('staffCode', e.target.value)} placeholder="EMP001" />
                </label>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">First Name *</div>
                  <input value={form.firstName} required maxLength={120}
                         onChange={e => setField('firstName', e.target.value)} />
                </label>
                <label style={{ flex: 1 }}>
                  <div className="muted">Last Name *</div>
                  <input value={form.lastName} required maxLength={120}
                         onChange={e => setField('lastName', e.target.value)} />
                </label>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">Email id *</div>
                  <input type="email" value={form.email} required maxLength={320}
                         onChange={e => setField('email', e.target.value)} placeholder="staff@college.edu" />
                </label>
                <label style={{ flex: 1 }}>
                  <div className="muted">Mobile No1 *</div>
                  <input value={form.mobile1} required maxLength={10} inputMode="numeric" pattern="\d{10}"
                         onChange={e => setField('mobile1', e.target.value.replace(/\D/g, ''))}
                         placeholder="9876543210" />
                </label>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: '0 0 220px' }}>
                  <div className="muted">Gender</div>
                  <select value={form.gender} onChange={e => setField('gender', e.target.value)}>
                    <option value="">—</option>
                    {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </label>
              </div>
              {formError && <div className="error">{formError}</div>}
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="primary" disabled={busy}>
                  {busy ? 'Saving…' : editId ? 'Save changes' : 'Create staff'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </OrgLayout>
  );
}
